<script setup lang='ts'>
import { X } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useFiltersStore } from '@/stores/useFiltersStore';
// import { useProductsStore } from '@/stores/useProductsStore';
import { hasActiveFilters } from '@/utils/utils';

const props = defineProps<{
  isOpen: boolean;
  title: string;
  anchor: 'top' | 'bottom';
  footer: boolean;
}>();

const emits = defineEmits(['close', 'validation', 'reset']);

const filtersStore = useFiltersStore();
const { filters } = storeToRefs(filtersStore);

// const productStore = useProductsStore();
// const { totalResults } = storeToRefs(productStore);

const { t } = useI18n();

const sliderAlignment = computed(() => props.anchor === 'top' ? 'inset-block-start:var(--offset-top);height:calc(100dvh - var(--offset-top));' : 'inset-block-start:55dvh;height:calc(100dvh - 55dvh);');
const gridComposition = computed(() => props.footer ? 'grid-template-rows: 65px auto 90px;' : 'grid-template-rows: 65px auto;');

watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    document.body.style.overflow = 'hidden';
  }
  else {
    document.body.style.overflow = '';
  }
});
</script>

<template>
  <div v-if="isOpen" class="backdrop">
    <section class="slider" :style="[sliderAlignment, gridComposition]" :aria-label="t('menu.sorting')">
      <div class="header">
        <slot name="header">
          <h3>{{ title }}</h3>
          <X
            :size="20" color="var(--yb-color-black)" tabindex="0" @click="emits('close')"
            @keydown.enter="emits('close')"
          />
        </slot>
      </div>
      <div class="body">
        <slot />
      </div>
      <div v-if="footer" class="footer">
        <slot name="footer">
          <button type="button" :disabled="!hasActiveFilters(filters)" class="cancel-button" @click="emits('reset')">
            {{ t('search.cancel_button') }}
          </button>
          <button type="button" class="validate-button" @click="emits('validation')">
            {{ t('search.button') }}
          </button>
        </slot>
      </div>
    </section>
  </div>
</template>

<style lang="css" scoped>
.backdrop {
  font-family: var(--yb-default-font, sans-serif);
  position: fixed;
  inset: 0;
  background-color: #00000050;
  backdrop-filter: blur(2px);
  z-index: 50;
}

.slider {
  --offset-top: 10vh;
  position: absolute;
  width: 100%;
  background-color: var(--yb-color-white);
  border-top-left-radius: var(--yb-radius-base);
  border-top-right-radius: var(--yb-radius-base);
  transform: translateY(100%);
  z-index: 60;
  display: grid;
  animation: slideUp 0.3s forwards;
  animation-delay: 0.1s;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);

  :where(.header, .body, .footer) {
    padding: var(--yb-spacing-base);
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid var(--yb-color-mediumgray);
  }

  h3 {
    font-size: 18px;
  }

  .body {
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
  }

  .footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-top: 1px solid var(--yb-color-mediumgray);

    .cancel-button,
    .validate-button {
      all: unset;
      text-align: center;
    }

    .cancel-button {
      font-weight: var(--yb-text-semi-bold);

      &:disabled {
        color: var(--yb-color-mediumgray);
        text-decoration: none;
      }
    }

    .validate-button {
      background-color: var(--yb-color-primary);
      color: var(--yb-color-white);
      font-weight: var(--yb-text-semi-bold);
      padding: 0.8rem var(--yb-spacing-sm);
      border-radius: var(--yb-radius-full);

      &:disabled {
        background-color: var(--yb-color-mediumgray);
        color: var(--yb-color-darkgray);
      }
    }
  }
}

@keyframes slideUp {
  to {
    transform: translateY(0);
  }
}
</style>
